import Vue from 'vue'
import axios from 'axios'
import instance from '@util/axios'
import { isCancel } from 'axios'

let isRefreshing = false
let refreshAndRetryQueue = []

const isErrorStatus = (error, status) => {
  return (
    status === (error.status || (error.response ? error.response.status : null))
  )
}

const checkNonFieldErrors = (errors) => {
  if (errors && 1 < errors.length) {
    Vue.prototype.$bus.$emit('error', {
      type: 'non-field',
      data: errors
    })
  }
}

const setup = (store) => {
  instance.interceptors.request.use(
    async (config) => {
      const accessToken = store.getters.accessToken

      const headerAuthorization = config.headers['Authorization'] || ''

      if (!!headerAuthorization || headerAuthorization.includes('Token ')) {
        return config
      } else if (!!accessToken) {
        config.headers['Accept'] = 'application/json'

        if (!!config.headers['TokenPrefix']) {
          config.headers[
            'Authorization'
          ] = `${config.headers['TokenPrefix']} ${accessToken}`

          delete config.headers['TokenPrefix']
        } else {
          config.headers['Authorization'] = `JWT ${accessToken}`
        }
      }

      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  instance.interceptors.response.use(
    (response) => {
      return response
    },
    async (error) => {
      const isAuthenticated = store.getters.authenticated
      const originalConfig = error.config

      const { status: statusCode } = error.response || {}

      if (isAuthenticated && 401 === statusCode) {
        if (!isRefreshing) {
          isRefreshing = true

          const refreshToken = store.getters.refreshToken

          try {
            const res = await axios.post(
              `${process.env.VUE_APP_BACKEND_URL}/api/v1/auth/token/refresh/`,
              {
                refresh: refreshToken
              }
            )

            const { access: newAccessToken, refresh: newRefreshToken } =
              res.data.data || {}

            if (!!originalConfig.headers.TokenPrefix) {
              originalConfig.headers.Authorization = `${originalConfig.headers.TokenPrefix} ${newAccessToken}`

              delete originalConfig.headers.TokenPrefix
            } else {
              originalConfig.headers.Authorization = `JWT ${newAccessToken}`
            }

            store.dispatch('setAccessToken', newAccessToken)
            store.dispatch('setRefreshToken', newRefreshToken)

            isRefreshing = false

            refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
              if (!!config.headers.TokenPrefix) {
                config.headers.Authorization = `${config.headers.TokenPrefix} ${newAccessToken}`

                delete config.headers.TokenPrefix
              } else {
                config.headers.Authorization = `JWT ${newAccessToken}`
              }

              instance
                .request(config)
                .then((response) => resolve(response))
                .catch((err) => reject(err))
            })

            refreshAndRetryQueue.length = 0

            return instance(originalConfig)
          } catch (_) {
            store.dispatch('setAccessToken', null)
            store.dispatch('setRefreshToken', null)

            store.dispatch('setUser', null)

            Vue.toasted.show('User logged out.', {
              type: 'error'
            })

            isRefreshing = false
          }
        }

        return new Promise((resolve, reject) => {
          refreshAndRetryQueue.push({
            config: originalConfig,
            resolve,
            reject
          })
        })
      }

      Vue.prototype.$bus.$emit('error-catched')

      //If the request is cancelled (by code), then just do nothing
      if (isCancel(error) || error.code === 'ERR_CANCELED') {
        return Promise.reject(error)
      }

      const errorData = error.response ? error.response.data : {}
      const supportEmail = process.env.VUE_APP_SUPPORT_EMAIL

      if (error.config && error.config.skipToast) {
        return Promise.reject(error)
      }

      if ('string' === typeof errorData) {
        if (JSON.parse(errorData).noToastError) {
          return Promise.reject(error)
        }
      }

      const getNonFieldErrors = () => {
        if (errorData.non_field_errors) {
          return errorData.non_field_errors
        } else if (errorData.error) {
          return errorData.error.non_field_errors || null
        } else if (errorData.data) {
          if (errorData.data.error) {
            return errorData.data.error.non_field_errors
          } else {
            return errorData.data.non_field_errors || null
          }
        } else {
          return null
        }
      }

      checkNonFieldErrors(getNonFieldErrors())

      if (isErrorStatus(error, 500)) {
        Vue.toasted.show(
          `There's a problem with the server. Please email us at ${supportEmail}`,
          {
            type: 'error'
          }
        )
      } else {
        let errorMessage = error.response
          ? error.response.statusText
          : 'Oops! Something went wrong.'

        if (!!errorData.email && !!errorData.email.error) {
          errorMessage = errorData.email.error
        } else if (!!errorData.message) {
          errorMessage = errorData.message
        } else if (!!errorData.detail) {
          errorMessage = errorData.detail
        } else if (!!errorData.error) {
          errorMessage(errorData.error)
        } else if (getNonFieldErrors() && getNonFieldErrors()[0]) {
          errorMessage = getNonFieldErrors()[0]
        } else if (Array.isArray(errorData) && errorData[0]) {
          errorMessage = errorData[0]
        } else if (!errorMessage) {
          errorMessage = 'field-error'
        }

        if ('field-error' === errorMessage) {
          Vue.prototype.$bus.$emit('field-error', errorData)
        } else {
          Vue.toasted.show(errorMessage, {
            type: 'error'
          })
        }
      }

      return Promise.reject(error)
    }
  )
}

export default setup
